import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, Eye, Users, AlertTriangle } from 'lucide-react';

const TermsOfService = () => {
  const lastUpdated = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long', 
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <Link 
              to="/register" 
              className="flex items-center text-blue-600 hover:text-blue-700 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Registration
            </Link>
            <div className="flex items-center text-gray-500">
              <FileText className="h-5 w-5 mr-2" />
              <span className="text-sm">Legal Document</span>
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Terms of Service
          </h1>
          <p className="text-gray-600">
            Service Agreement for Incident Management System
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Last updated: {lastUpdated}
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="prose max-w-none">
            
            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Users className="h-5 w-5 mr-2 text-blue-600" />
                Service Agreement
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Welcome to our Incident Management System. By creating an account and using our services, 
                you agree to be bound by these Terms of Service, our Privacy Policy, and all applicable 
                laws and regulations.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 text-sm">
                  <strong>Important:</strong> Please read these terms carefully. By checking the service 
                  agreement box during registration, you acknowledge that you have read, understood, and 
                  agree to be bound by these terms.
                </p>
              </div>
            </section>

            {/* Account Terms */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">1. Account Registration</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• You must provide accurate and complete information during registration</li>
                <li>• You are responsible for maintaining the security of your account credentials</li>
                <li>• You must be at least 18 years old to create an account</li>
                <li>• One person may not maintain multiple accounts</li>
                <li>• You must notify us immediately of any unauthorized access to your account</li>
              </ul>
            </section>

            {/* Service Usage */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">2. Acceptable Use</h3>
              <div className="space-y-3 text-gray-700">
                <p>You agree to use our incident management system only for legitimate business purposes:</p>
                <ul className="space-y-2 ml-4">
                  <li>• Creating and managing incident reports</li>
                  <li>• Collaborating with team members on incident resolution</li>
                  <li>• Accessing reporting and analytics features</li>
                  <li>• Communicating through our messaging systems</li>
                </ul>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                  <h4 className="font-medium text-red-800 mb-2 flex items-center">
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Prohibited Activities
                  </h4>
                  <ul className="space-y-1 text-red-700 text-sm">
                    <li>• Attempting to gain unauthorized access to our systems</li>
                    <li>• Uploading malicious software or harmful content</li>
                    <li>• Using the system to harass or threaten other users</li>
                    <li>• Sharing login credentials with unauthorized individuals</li>
                    <li>• Attempting to reverse engineer or copy our software</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Data and Privacy */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <Shield className="h-5 w-5 mr-2 text-green-600" />
                3. Data Protection & Privacy
              </h3>
              <div className="space-y-3 text-gray-700">
                <p>Your privacy and data security are important to us:</p>
                <ul className="space-y-2 ml-4">
                  <li>• We collect only necessary information to provide our services</li>
                  <li>• Your data is encrypted and stored securely</li>
                  <li>• We do not sell your personal information to third parties</li>
                  <li>• You can request data export or deletion at any time</li>
                  <li>• We comply with applicable data protection regulations</li>
                </ul>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 text-sm">
                    <strong>Data Processing:</strong> By using our service, you consent to the processing 
                    of your personal data as described in our Privacy Policy, including incident reports, 
                    communication logs, and usage analytics.
                  </p>
                </div>
              </div>
            </section>

            {/* Service Availability */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">4. Service Availability</h3>
              <div className="space-y-3 text-gray-700">
                <p>We strive to provide reliable service but cannot guarantee 100% uptime:</p>
                <ul className="space-y-2 ml-4">
                  <li>• Scheduled maintenance may cause temporary service interruptions</li>
                  <li>• We aim for 99.5% uptime during business hours</li>
                  <li>• Critical incidents will be resolved with priority</li>
                  <li>• Service status updates are available on our status page</li>
                </ul>
              </div>
            </section>

            {/* Support and Communication */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">5. Support & Communication</h3>
              <div className="space-y-3 text-gray-700">
                <p>We provide support through multiple channels:</p>
                <ul className="space-y-2 ml-4">
                  <li>• In-app help and documentation</li>
                  <li>• Email support during business hours</li>
                  <li>• Priority support for critical incidents</li>
                  <li>• Training resources and user guides</li>
                </ul>
              </div>
            </section>

            {/* Liability and Disclaimers */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">6. Limitation of Liability</h3>
              <div className="space-y-3 text-gray-700">
                <p>
                  Our service is provided "as is" without warranties of any kind. We are not liable 
                  for indirect, incidental, or consequential damages arising from use of our service.
                </p>
                <p>
                  You are responsible for maintaining backups of your important data and ensuring 
                  your own business continuity processes.
                </p>
              </div>
            </section>

            {/* Account Termination */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">7. Account Termination</h3>
              <div className="space-y-3 text-gray-700">
                <p>Either party may terminate the service relationship:</p>
                <ul className="space-y-2 ml-4">
                  <li>• You may deactivate your account at any time</li>
                  <li>• We may suspend accounts that violate these terms</li>
                  <li>• 30 days notice will be provided for non-urgent terminations</li>
                  <li>• Data export is available for 90 days after termination</li>
                </ul>
              </div>
            </section>

            {/* Changes to Terms */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">8. Changes to These Terms</h3>
              <div className="space-y-3 text-gray-700">
                <p>
                  We may update these terms periodically. Users will be notified of significant 
                  changes via email or in-app notifications. Continued use of the service after 
                  changes constitutes acceptance of the new terms.
                </p>
              </div>
            </section>

            {/* Contact Information */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">9. Contact Information</h3>
              <div className="space-y-3 text-gray-700">
                <p>For questions about these terms or our service:</p>
                <ul className="space-y-2 ml-4">
                  <li>• Email: legal@yourcompany.com</li>
                  <li>• Support: support@yourcompany.com</li>
                  <li>• Phone: +1 (555) 123-4567</li>
                  <li>• Address: 123 Business St, City, State 12345</li>
                </ul>
              </div>
            </section>

            {/* Governing Law */}
            <section className="mb-8 pb-8 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">10. Governing Law</h3>
              <p className="text-gray-700">
                These terms are governed by the laws of [Your State/Country]. Any disputes will be 
                resolved through binding arbitration in [Your Jurisdiction].
              </p>
            </section>

            {/* Footer */}
            <div className="text-center pt-6">
              <p className="text-gray-500 text-sm mb-4">
                By creating an account, you acknowledge that you have read and understood these terms.
              </p>
              <div className="flex justify-center space-x-4">
                <Link 
                  to="/register" 
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  Back to Registration
                </Link>
                <Link 
                  to="/privacy-policy" 
                  className="text-blue-600 hover:text-blue-700 px-6 py-2 transition-colors"
                >
                  Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;